import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Spin, Alert } from 'antd';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { multichoixService } from '../../../services/multichoixService';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';

/**
 * Split comma-separated IDs into individual IDs
 */
const splitIds = (id) => {
  if (!id) return [];
  if (typeof id === 'string' && id.includes(',')) {
    return id.split(',').map((i) => i.trim()).filter(Boolean);
  }
  return [id];
};

/**
 * Check if an option is selected (ANY of its IDs are in selectedIds)
 */
const isOptionSelected = (optionId, selectedIds) => {
  const ids = splitIds(optionId);
  return ids.some((id) => selectedIds.includes(id));
};

const InterventionPeriodStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  onChange,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  const [selectedIds, setSelectedIds] = useState(value || []);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  // Sync selectedIds with value prop
  useEffect(() => {
    setSelectedIds(value || []);
  }, [value]);

  // Transform selections for API - includes ALL previous selections
  const apiSelections = multichoixService.transformSelectionsForApi(allSelections);
  const hasCultures = apiSelections.cultures.length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'multichoix-step-5',
      apiSelections.cultures,
      apiSelections.cultural_practices,
      apiSelections.treatment_type,
      apiSelections.enemies,
    ],
    queryFn: () => multichoixService.getStepOptions(5, apiSelections),
    enabled: hasCultures,
    staleTime: 5 * 60 * 1000,
  });

  const periods = data?.options || [];

  // Mobile: Get display value
  const mobileDisplayValue = useMemo(() => {
    const selectedCount = periods.filter((p) => isOptionSelected(p.id, selectedIds)).length;
    if (selectedCount === 0) return '';
    if (selectedCount === periods.length) return t('multichoix.steps.intervention_period.all_selected', 'Toutes les périodes');
    return `${selectedCount} ${t('multichoix.common.selected', 'sélectionné(s)')}`;
  }, [selectedIds, periods, t]);

  // Mobile: Filter options
  const filteredPeriods = useMemo(() => {
    if (!mobileSearchValue) return periods;
    const search = mobileSearchValue.toLowerCase();
    return periods.filter((p) =>
      p.label.toLowerCase().includes(search)
    );
  }, [periods, mobileSearchValue]);

  const handleItemChange = useCallback((itemId, itemLabel, checked) => {
    const itemIds = splitIds(itemId);
    let newIds;
    let newLabels;

    if (checked) {
      newIds = [...new Set([...selectedIds, ...itemIds])];
      newLabels = [
        ...(selectionLabels || []),
        { value: itemIds[0], label: itemLabel },
      ];
    } else {
      newIds = selectedIds.filter((id) => !itemIds.includes(id));
      newLabels = (selectionLabels || []).filter((l) => !itemIds.includes(l.value));
    }

    setSelectedIds(newIds);
    onChange(newIds, newLabels);
  }, [selectedIds, selectionLabels, onChange]);

  if (!hasCultures) {
    return (
      <Alert
        type="info"
        message={t('multichoix.steps.intervention_period.select_previous', 'Complétez les étapes précédentes')}
        showIcon
      />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('common.error', 'Une erreur est survenue')}
        description={error.message}
        showIcon
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip={t('common.loading', 'Chargement...')} />
      </div>
    );
  }

  if (periods.length === 0) {
    return (
      <Alert
        type="warning"
        message={t('multichoix.steps.intervention_period.no_options', 'Aucune période d\'intervention disponible')}
        showIcon
        className="text-xs sm:text-sm"
      />
    );
  }

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || t(step.placeholder)}
          </span>
          {isLoading ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t(step.title)}
          showDone
          onDone={() => setSheetOpen(false)}
        >
          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={(e) => setMobileSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isLoading && filteredPeriods.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {!isLoading && filteredPeriods.length > 0 && (
              <div className="py-1">
                {filteredPeriods.map((period) => {
                  const isSelected = isOptionSelected(period.id, selectedIds);
                  return (
                    <div
                      key={period.id}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() => handleItemChange(period.id, period.label, !isSelected)}
                    >
                      <div
                        className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                          isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">{period.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="space-y-1 sm:space-y-2 max-h-[250px] sm:max-h-[300px] overflow-y-auto green-checkbox">
      {periods.map((period) => (
        <div key={period.id} className="py-1">
          <Checkbox
            checked={isOptionSelected(period.id, selectedIds)}
            onChange={(e) =>
              handleItemChange(period.id, period.label, e.target.checked)
            }
            className="text-xs sm:text-sm"
          >
            <span className="text-xs sm:text-sm">{period.label}</span>
          </Checkbox>
        </div>
      ))}
    </div>
  );
};

export default InterventionPeriodStepContent;
